import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { showError, showSuccess } from '../../../../utils/Notifications';
import blockService from '../services/block.service';
import { EntityBlockMappingSaveRequestDto } from '../dto/request/entity-block-mapping-save-request.dto';
import { ImageBlockSaveRequestDto } from '../dto/request/image-block-save-request.dto';
import { RichTextBlockSaveRequestDto } from '../dto/request/rich-text-block-save-request.dto';
import { ImageBlockResponseDto } from '../dto/response/image-block-response.dto';
import { RichTextBlockResponseDto } from '../dto/response/rich-text-block-response.dto';
import { ImageBlockUpdateRequestDto } from '../dto/request/image-block-update-request.dto';
import { RichTextBlockUpdateRequestDto } from '../dto/request/rich-text-block-update-request.dto';
import { EntityBlockMappingResponseDto } from '../dto/response/entity-block-mapping-response.dto';

const initialState = {
  imageBlockStatus: 'idle',
  richTextBlockStatus: 'idle',
  status: 'idle',
  blocks: [] as EntityBlockMappingResponseDto[] | null,
  imageBlock: {} as ImageBlockResponseDto | null,
  richTextBlock: {} as RichTextBlockResponseDto | null,
  error: null as string | null,
};

export const fetchBlocks = createAsyncThunk(
  'organizationBlocks/fetchBlocks',
  async (id: string) => {
    return await blockService.fetchBlocks(id);
  }
);

export const fetchImageBlockById = createAsyncThunk(
  'organizationBlocks/fetchImageBlockById',
  async (id: string) => {
    return await blockService.fetchImageBlockById(id);
  }
);

export const fetchRichTextBlockById = createAsyncThunk(
  'organizationBlocks/fetchRichTextBlockById',
  async (id: string) => {
    return await blockService.fetchRichTextBlockById(id);
  }
);

export const saveImageBlock = createAsyncThunk(
  'organizationBlocks/saveImageBlock',
  async (saveRequest: ImageBlockSaveRequestDto) => {
    return await blockService.saveImageBlock(saveRequest);
  }
);

export const saveRichTextBlock = createAsyncThunk(
  'organizationBlocks/saveRichTextBlock',
  async (saveRequest: RichTextBlockSaveRequestDto) => {
    return await blockService.saveRichTextBlock(saveRequest);
  }
);

export const orderBlocks = createAsyncThunk(
  'organizationBlocks/orderBlocks',
  async (saveRequest: EntityBlockMappingSaveRequestDto[]) => {
    return await blockService.orderBlocks(saveRequest);
  }
);

export const updateImageBlock = createAsyncThunk(
  'organizationBlocks/updateImageBlock',
  async (updateRequest: ImageBlockUpdateRequestDto, { rejectWithValue }) => {
    try {
      return await blockService.updateImageBlock(updateRequest);
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

export const updateRichTextBlock = createAsyncThunk(
  'organizationBlocks/updateRichTextBlock',
  async (updateRequest: RichTextBlockUpdateRequestDto, { rejectWithValue }) => {
    try {
      return await blockService.updateRichTextBlock(updateRequest);
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

export const deleteBlock = createAsyncThunk(
  'organizationBlocks/deleteBlock',
  async (
    params: { existingId: string; organizationId: string },
    { rejectWithValue }
  ) => {
    const { existingId, organizationId } = params;
    try {
      await blockService.deleteBlock(existingId, organizationId);
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const organizationBlockSlice = createSlice({
  name: 'organizationBlockSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBlocks.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchBlocks.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.blocks = action.payload;
      })
      .addCase(fetchBlocks.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchImageBlockById.pending, (state) => {
        state.imageBlockStatus = 'loading';
      })
      .addCase(fetchImageBlockById.fulfilled, (state, action) => {
        state.imageBlockStatus = 'succeeded';
        state.imageBlock = action.payload;
      })
      .addCase(fetchImageBlockById.rejected, (state, action) => {
        state.imageBlockStatus = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchRichTextBlockById.pending, (state) => {
        state.richTextBlockStatus = 'loading';
      })
      .addCase(fetchRichTextBlockById.fulfilled, (state, action) => {
        state.richTextBlockStatus = 'succeeded';
        state.richTextBlock = action.payload;
      })
      .addCase(fetchRichTextBlockById.rejected, (state, action) => {
        state.richTextBlockStatus = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(saveImageBlock.pending, (state) => {
        state.imageBlockStatus = 'loading';
      })
      .addCase(saveImageBlock.fulfilled, (state, action) => {
        state.imageBlockStatus = 'succeeded';
        showSuccess('Image block added successfully.');
      })
      .addCase(saveImageBlock.rejected, (state, action) => {
        state.imageBlockStatus = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(saveRichTextBlock.pending, (state) => {
        state.richTextBlockStatus = 'loading';
      })
      .addCase(saveRichTextBlock.fulfilled, (state, action) => {
        state.richTextBlockStatus = 'succeeded';
        showSuccess('Richtext block added successfully.');
      })
      .addCase(saveRichTextBlock.rejected, (state, action) => {
        state.richTextBlockStatus = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(orderBlocks.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(orderBlocks.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.blocks = action.payload;
      })
      .addCase(orderBlocks.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(updateImageBlock.pending, (state) => {
        state.imageBlockStatus = 'loading';
      })
      .addCase(updateImageBlock.fulfilled, (state, action) => {
        state.imageBlockStatus = 'succeeded';
        showSuccess('Image block saved successfully.');
      })
      .addCase(updateImageBlock.rejected, (state, action) => {
        state.imageBlockStatus = 'failed';
        state.error = action.payload as string;
        showError(`Updating image block failed. ${action.payload as string}`);
      })
      .addCase(updateRichTextBlock.pending, (state) => {
        state.richTextBlockStatus = 'loading';
      })
      .addCase(updateRichTextBlock.fulfilled, (state, action) => {
        state.richTextBlockStatus = 'succeeded';
        showSuccess('Text block saved successfully.');
      })
      .addCase(updateRichTextBlock.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Updating text block failed. ${action.payload as string}`);
      })
      .addCase(deleteBlock.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteBlock.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Block deleted successfully.');
      })
      .addCase(deleteBlock.rejected, (state, action) => {
        state.richTextBlockStatus = 'failed';
        state.error = action.payload as string;
        showError(`Deleting block failed. ${action.payload as string}`);
      });
  },
});

export default organizationBlockSlice.reducer;
